<template>
  <div
    class="itemStockinfoWatchlistDv"
    :class="{ fluidWrapper: isFluid }"
    v-if="stock && trade"
  >
    <IconComponent
      class="iconDv"
      :class="{ removeOnRightCenter: staticRemove }"
      @click.stop="onRemove()"
      @touchstart="onRemove()"
      :id="`minusWining${index}`"
      v-if="showRemove"
      icon="minus.svg"
    />
    <div class="firstDv" :class="{ noChartItem: !showChart }">
      <StockImageTag
        :stockId="trade.stockId || trade._id"
        :imgUrl="stock.logoUrl"
        :symbol="stock.symbol"
        :market="trade.market || trade.type || 'stocks'"
        :key="trade.stockId"
      />
      <div class="textDv">
        <h2>{{ stock?.symbol ?? "-" }}</h2>

        <el-tooltip
          v-if="shouldShowTooltip"
          effect="dark"
          :content="stock.name"
          placement="top-start"
        >
          <p ref="textElement" class="no-rtl" @mouseover="checkOverflow">
            {{ stock.name ?? "-" }}
          </p>
        </el-tooltip>
        <p v-else ref="textElement" class="no-rtl" @mouseover="checkOverflow">
          {{ stock.name ?? "-" }}
        </p>
      </div>
    </div>
    <div v-if="showChart" class="middleDv">
      <SparklineComponent
        :key="stock?._id + stock?.changesPercentage"
        :isPositive="stock?.changesPercentage >= 0"
        :data="stock?.fiveDayTrend"
      />
    </div>
    <div class="endDv">
      <div class="lastDv">
        <h2
          :key="stock?._id + stock?.price"
          ref="priceElement"
          v-html="
            $filters.systemNumberConvention(
              stock?.price,
              true,
              false,
              widthCalculating
            )
          "
        ></h2>
        <div
          :class="{ downward: stock?.changesPercentage < 0 }"
          class="informationGridWatchlistViewDv"
        >
          <el-tooltip
            class="box-item"
            effect="dark"
            :content="'$' + stock.change"
            placement="top-start"
          >
            <div>
              <IconComponent
                class="priceIcon"
                icon="stock-pricing-up-icon.svg"
              />
              {{
                Math.abs(
                  $filters.systemNumberConvention(
                    stock.changesPercentage,
                    false
                  )
                )
              }}%
            </div>
          </el-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import IconComponent from "@/components/IconComponent.vue";
import { useSocketStockData } from "@/composables/stock_data.composable";
import { useStockSocket } from "@/composables/stock_socket.composable";
import utilService from "@/services/util.service";
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";

export default defineComponent({
  props: {
    trade: Object,
    showRemove: Boolean,
    staticRemove: Boolean,
    isFluid: Boolean,
    market: String,
    showChart: {
      default: true,
      type: Boolean,
    },
  },
  components: {
    IconComponent,
  },
  emits: ["onRemove"],
  setup(props, context) {
    onMounted(() => {});

    const onRemove = () => context.emit("onRemove");
    const priceCheckInterval = ref<NodeJS.Timeout>();
    const priceElement = ref<HTMLElement | null>(null);
    const widthCalculating = ref();

    const { addStockId, checkStockExistence } = useStockSocket();

    const initialObject = () => {
      let obj = {
        _id: props.trade?.stockId || props.trade?.stock_id || props.trade?._id,
        symbol: props.trade?.symbol,
        companyName: props.trade?.name,
        last: props.trade?.price || 0, // Default to 0 instead of undefined
        change: props.trade?.change || 0,
        changePercent: props.trade?.changesPercentage || 0,
        market: props.trade?.type,
        showTrendChart: true,
        fiveDayTrend: [],
        price: props.trade?.price || 0,
      };
      if (!checkStockExistence(props.trade?.stockId)) {
        addStockId({ showTrendChart: true, stockId: props.trade?.stockId });
      }
      return obj;
    };

    const { stockData: stock, updateStockInfoObject } = useSocketStockData(
      initialObject()
    );

    watch(
      () => props.trade,
      async (newStockData) => {
        updateStockInfoObject(initialObject());
        await nextTick(); // Ensure DOM updates immediately
      },
      { deep: true }
    );

    watch(
      () => stock.value,
      async (newStockData) => {
        if (!newStockData) return;

        // If price is undefined or null, or if we're missing critical data
        if (newStockData.price === undefined || newStockData.price === null) {
          console.log(
            "Refetching stock data due to missing price",
            newStockData
          );
          updateStockInfoObject(initialObject());

          // Add a small delay before checking again to avoid rapid refetching
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Check if price is still undefined after delay
          if (stock.value?.price === undefined) {
            console.log("Price still undefined after refetch, trying again");
            updateStockInfoObject(initialObject());
          }
        }
      },
      { deep: true }
    );

    onMounted(() => {
      if (priceElement.value) {
        const width = priceElement.value.offsetWidth;
        widthCalculating.value = width;
      }
      updateStockInfoObject(initialObject());
      priceCheckInterval.value = setInterval(() => {
        if (stock.value?.price === undefined) {
          console.log("Periodic check found undefined price, refetching");
          updateStockInfoObject(initialObject());
        }
      }, 5000); // Check every 5 seconds
    });

    watch(priceElement, () => {
      if (priceElement.value) {
        const width = priceElement.value.offsetWidth;
        widthCalculating.value = width;
      }
    });

    const textElement = ref<HTMLElement | null>(null);

    const shouldShowTooltip = ref(false);

    const checkOverflow = () => {
      const el = textElement.value;
      if (el) {
        shouldShowTooltip.value = el.scrollWidth > el.clientWidth;
      }
    };
    onUnmounted(() => {
      if (priceCheckInterval.value) {
        clearInterval(priceCheckInterval.value);
      }
    });
    onMounted(() => {
      checkOverflow(); // Check overflow on mount
    });
    const isSmallScreen = ref(utilService.isSmallScreen());
    const stockImageSize = computed(() => {
      if (isSmallScreen) {
        return {
          // width: "28px",
          // height: "28px",
        };
      }
    });
    return {
      priceElement,
      widthCalculating,
      stock,
      onRemove,
      textElement,
      shouldShowTooltip,
      checkOverflow,
      stockImageSize,
    };
  },
});
</script>
<style lang="scss" scoped>
.itemStockinfoWatchlistDv {
  border: 1px solid transparent;
  background: var(--base-light);
  border-radius: 10px;
  padding: var(--section-inner-vertical-spacing);
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0px 0px var(--section-inner-vertical-spacing);
  gap: 10px;
  margin: 0;
  width: 430px;
  padding: 10px 15px;
  box-sizing: border-box;
  position: relative;
  border: 1px solid var(--base-new-border);
  @media (max-width: 767px) {
    width: 100%;
  }
  &.fluidWrapper {
    width: 100%;
  }

  cursor: pointer;

  .iconDv {
    width: 16px;
    height: 18px;

    &.removeOnRightCenter {
      position: static;
      max-width: 18px;
      flex: 0 0 18px;
      height: 22px;
      margin-inline-start: 5px;
      order: 4;
    }
  }

  .firstDv {
    width: 33.33%;
    max-width: 33.33%;

    &.noChartItem {
      width: 61%;
      max-width: 61%;
    }

    display: flex;
    align-items: center;
    gap: var(--tile-horizontal-gap);
    justify-content: flex-start;

    figure {
      border: 4px solid transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--white-color);
      padding: 4px;
      border-radius: 10px;
      flex: 0 0 35px;
      width: 35px;
      height: 35px;
      overflow: visible;
      border-color: var(--color-icon-check);
      cursor: pointer;
      margin: 0 10px 0 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 10px;
      }
    }

    .textDv {
      overflow: hidden;

      h2 {
        @include stockSymbol();
        margin-top: 5px;
        font-size: var(--font-2md);
        color: var(--text-color);
      }

      p {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        letter-spacing: 0px;
        padding: 0px;
        font-size: var(--font-2sm);
        color: var(--color-new-light-gray8);
        margin: 0;
        font-weight: 400;
      }
    }
  }

  .middleDv {
    width: 28%;
    max-width: 28%;
    display: flex;
    align-items: center;
    justify-content: center;

    .sparkline_container {
      max-height: 30px;
      max-width: 70px;
      min-width: unset;
    }
  }

  .endDv {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 38%;
    max-width: 38%;
    margin-inline-start: auto;

    .lastDv {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-direction: column;

      h2 {
        @include stockPrice();
        line-height: normal;
        text-align: right;
        width: 100%;
        font-size: var(--font-2sm);
      }

      span {
        @include changePercent();
        line-height: normal;
        font-size: var(--font-2sm);
        width: 100%;
      }

      .informationGridWatchlistViewDv {
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        color: var(--recommend-bullish);
        line-height: normal;
        font-size: var(--font-2sm);
        width: 100%;

        &.downward {
          color: var(--color-negative);
          fill: var(--color-negative);
          i {
            color: var(--color-negative);
            fill: var(--color-negative);
          }

          h2 {
            color: var(--color-negative);
          }

          .priceIcon {
            color: var(--color-negative);
            fill: var(--color-negative);
            transform: rotate(180deg);
          }
        }

        h2 {
          font-weight: 500;
          color: var(--recommend-bullish);
          line-height: normal;
          font-size: var(--font-2sm);
        }

        .priceIcon {
          color: var(--recommend-bullish);
          fill: var(--recommend-bullish);
          width: 10px;
          flex: 0 0 10px;
          height: 10px;
          margin: 0px 1px 0px 0px;
        }
        i {
          color: var(--recommend-bullish);
          fill: var(--recommend-bullish);
        }
      }
    }

    .iconmiunusDv {
      width: 18px;
      height: 18px;
      margin-inline-start: 15px;
    }
  }

  &:last-child {
    margin: 0px;
  }

  &.selected {
    border-color: var(--orange);
  }
}

@media screen and (max-width: 767px) {
  .itemStockinfoWatchlistDv .endDv .lastDv span {
    font-size: var(--font-2xs);
  }
}

@media screen and (min-width: 1280px) and (max-width: 1891px) {
  .itemStockinfoWatchlistDv {
    padding: 6px 10px;
    gap: 4px;

    .firstDv {
      gap: 4px;

      &.noChartItem {
        width: 57%;
        max-width: 57%;
      }

      .textDv {
        h2 {
          font-size: var(--font-xs);
        }

        p {
          font-size: var(--font-2xs);
        }
      }
    }

    .endDv {
      .lastDv {
        .informationGridWatchlistViewDv {
          align-items: flex-end;
          font-size: var(--font-2xs);
          margin-top: -2px;

          h2 {
            font-size: var(--font-2xs);
          }

          .priceIcon {
            height: 4px !important;
            align-self: center;
            margin-top: 5px;
          }
        }

        > h2 {
          font-size: var(--font-xs);
        }
      }
    }
  }

  :deep(.firstDv) {
    .stock_logo {
      width: 30px !important;
      flex: 0 0 30px !important;
      height: 30px !important;
      border-width: 2px !important;
      margin-inline-end: 4px !important;
    }
  }
}
</style>
